/**
 * Format a date string to a readable format
 * @param dateString - ISO date string to format
 * @param compact - Whether to use a compact format
 * @returns Formatted date string
 */
export const formatDate = (dateString?: string, compact: boolean = false): string => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '—';
    }
    
    // Format to M/D/YY without leading zeros (e.g., 6/3/25)
    const month = date.getMonth() + 1; // getMonth() is 0-indexed
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2); // Get just the last 2 digits
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
};

/**
 * Format a date to show relative time (e.g., "5 minutes ago", "2 days ago")
 * @param dateString - ISO date string to format
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '—';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Convert to seconds
    const diffSec = Math.floor(diffMs / 1000);
    
    // Less than a minute
    if (diffSec < 60) {
      return 'just now';
    }
    
    // Less than an hour
    if (diffSec < 3600) {
      const minutes = Math.floor(diffSec / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    
    // Less than a day
    if (diffSec < 86400) {
      const hours = Math.floor(diffSec / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    
    // Less than a month (approx. 30 days)
    if (diffSec < 2592000) {
      const days = Math.floor(diffSec / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    // Format with regular date
    return formatDate(dateString);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '—';
  }
};

// Define User interface for type safety
interface User {
  first_name?: string;
  last_name?: string;
  username: string;
}

// Get user full name
export const getUserFullName = (user: User): string => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  
  if (user.first_name) {
    return user.first_name;
  }
  
  if (user.last_name) {
    return user.last_name;
  }
  
  return user.username;
};

// Get tenant level label
export const getTenantLevelLabel = (level: string): string => {
  // Return the actual level value (L0, L1, L2, L3) directly
  return level || '—';
};

// Get tenant type label
export const getTenantTypeLabel = (type: string): string => {
  switch (type) {
    case 'org':
      return 'Organization';
    case 'partner':
      return 'Partner';
    default:
      return type;
  }
};

// Get status badge color
export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'inactive':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}; 